import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@/utils/apiClient';
import { AUTH_ENDPOINTS, AUTH_STATUS, SESSION_TYPES } from '@/features/auth/constants';
import type {
    AuthState,
    AuthContext,
    AvailableContexts,
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    RegisterResponse,
    MeResponse,
    SwitchContextRequest,
    SwitchContextResponse,
    QuotaInfo,
} from '@/features/auth/types';
import type { SessionType, AuthProviderType } from '@/features/auth/constants';
import { queryClient } from '@/lib/queryClient';
import { setAuthToken, clearAuthToken, removeStoreUrl } from '@/utils/auth';
import { clearImpersonationRedirectHint } from '@/utils/impersonationHint';
import { resetFeatureBlockedState } from '@/utils/apiClient';

// ─── Initial State ────────────────────────────────────────────────

const initialContext: AuthContext = {
    merchantId: null,
    storeId: null,
    roles: [],
    permissions: [],
};

const initialAvailableContexts: AvailableContexts = {
    merchants: [],
    stores: [],
};

const initialState: AuthState = {
    status: AUTH_STATUS.INITIALIZING,
    user: null,
    context: initialContext,
    availableContexts: initialAvailableContexts,
    sessionType: null,
    authProvider: null,
    isImpersonating: false,
    adminUserId: null,
    needsPasswordSetup: false,
    quota: null,
    quotaRevalidated: false,
};

// ─── Async Thunks ─────────────────────────────────────────────────

/**
 * Called on app mount. Checks if the user has an active session
 * by calling GET /auth/me. The httpOnly cookie is sent automatically.
 */
export const initializeAuth = createAsyncThunk(
    'auth/initializeAuth',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiClient.get<{ data: MeResponse }>(AUTH_ENDPOINTS.ME);
            const me = response.data.data;
            // Real session loaded — drop reload hint so sidebar uses Redux only
            clearImpersonationRedirectHint();
            return me;
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 401) {
                return rejectWithValue('unauthenticated');
            }
            // 403 feature_not_enabled = plan downgraded, NOT an auth failure.
            // Let the user stay authenticated so the block screen can render.
            if (status === 403) {
                const errCode = error?.response?.data?.error;
                if (errCode === 'feature_not_enabled') {
                    return rejectWithValue('feature_not_enabled');
                }
                return rejectWithValue('access_denied');
            }
            return rejectWithValue(error?.response?.data?.message || 'Failed to initialize auth');
        }
    }
);

/**
 * Login with email/password. Backend sets httpOnly cookie on success.
 */
export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async (credentials: LoginRequest, { rejectWithValue }) => {
        try {
            const response = await apiClient.post<{ data: LoginResponse }>(
                AUTH_ENDPOINTS.LOGIN,
                credentials
            );
            return response.data.data;
        } catch (error: any) {
            const errData = error?.response?.data;
            const message =
                errData?.message ||
                (typeof errData?.error === 'string' ? errData.error : errData?.error?.message) ||
                'Login failed';

            // Detect multi-workspace ambiguity error
            if (
                message.toLowerCase().includes('multiple merchant') ||
                message.toLowerCase().includes('merchantid is required')
            ) {
                const availableMerchants =
                    error?.response?.data?.data?.availableMerchants ||
                    error?.response?.data?.availableMerchants ||
                    [];
                return rejectWithValue({
                    message,
                    multiWorkspace: true,
                    availableMerchants,
                });
            }

            return rejectWithValue(message);
        }
    }
);

/**
 * Self-service merchant registration.
 */
export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async (data: RegisterRequest, { rejectWithValue }) => {
        try {
            const response = await apiClient.post<{ data: RegisterResponse }>(
                AUTH_ENDPOINTS.REGISTER,
                data
            );
            const result = response.data.data;

            // If email verification is required, return the verification info
            // (no session is created yet, so we don't populate auth state)
            if (result?.requiresVerification) {
                return {
                    requiresVerification: true,
                    email: result.email || data.email,
                    message: result.message,
                } as RegisterResponse;
            }

            return result;
        } catch (error: any) {
            const errData = error?.response?.data;
            const message =
                errData?.message ||
                (typeof errData?.error === 'string' ? errData.error : errData?.error?.message) ||
                'Registration failed';
            return rejectWithValue(message);
        }
    }
);

/**
 * Logout. Clears session on backend, clears httpOnly cookie.
 */
export const logoutUser = createAsyncThunk(
    'auth/logoutUser',
    async (_, { rejectWithValue }) => {
        try {
            await apiClient.post(AUTH_ENDPOINTS.LOGOUT);
        } catch {
            // Even if logout API fails, continue clearing local state
        }
        // Always clear token and cache regardless of API success
        clearAuthToken();
        clearImpersonationRedirectHint();
        resetFeatureBlockedState();
        removeStoreUrl();
        try {
            localStorage.removeItem('available_contexts');
            localStorage.removeItem('impersonation_context');
            localStorage.removeItem('team_store_access');
        } catch {}
        queryClient.clear();
        return true;
    }
);

/**
 * Switch to a different merchant or store context.
 */
export const switchContext = createAsyncThunk(
    'auth/switchContext',
    async (data: SwitchContextRequest, { rejectWithValue }) => {
        try {
            const response = await apiClient.post<{ data: SwitchContextResponse }>(
                AUTH_ENDPOINTS.SWITCH_CONTEXT,
                data
            );
            // Remove all React Query caches since context changed.
            // Using removeQueries() instead of invalidateQueries() to prevent
            // stale data from a previous account being shown while refetching.
            queryClient.removeQueries();
            resetFeatureBlockedState();
            return response.data.data;
        } catch (error: any) {
            const errData = error?.response?.data;
            const message =
                errData?.message ||
                (typeof errData?.error === 'string' ? errData.error : errData?.error?.message) ||
                'Failed to switch context';
            return rejectWithValue(message);
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        /**
         * Force set auth status (used by 401 interceptor to clear state)
         */
        clearAuth: (state) => {
            state.status = AUTH_STATUS.UNAUTHENTICATED;
            state.user = null;
            state.context = initialContext;
            state.availableContexts = initialAvailableContexts;
            state.sessionType = null;
            state.authProvider = null;
            state.needsPasswordSetup = false;
        },

        /**
         * Update available contexts (e.g. after connecting a new store)
         */
        updateAvailableContexts: (
            state,
            action: PayloadAction<Partial<AvailableContexts>>
        ) => {
            if (action.payload.merchants) {
                state.availableContexts.merchants = action.payload.merchants;
            }
            if (action.payload.stores) {
                state.availableContexts.stores = action.payload.stores;
            }
        },

        /**
         * Update merchant-level feature flags in availableContexts.
         * Used to reflect admin-side feature toggles instantly without requiring refresh.
         */
        setMerchantFeatures: (
            state,
            action: PayloadAction<{ merchantId: string; features: Record<string, boolean> }>
        ) => {
            const { merchantId, features } = action.payload;
            const idx = state.availableContexts.merchants.findIndex(
                (m: any) => m.merchantId === merchantId
            );
            if (idx >= 0) {
                (state.availableContexts.merchants[idx] as any).features = features;
            }
        },

        // ── Backward-compatible legacy reducers ──────────────────────
        // These will be removed after the full migration is complete.

        /**
         * @deprecated Legacy compat — accepts partial store/user details updates.
         */
        updateStoreDetails: (state, action: PayloadAction<Record<string, any>>) => {
            const payload = action.payload;
            if (payload.merchantId) {
                state.context.merchantId = payload.merchantId;
            }
            if (payload.storeId) {
                state.context.storeId = payload.storeId;
            }
            if (payload.storeUrl && state.user) {
                // storeUrl is informational only
            }
            if (payload.userEmail && state.user) {
                state.user.email = payload.userEmail;
            }
            // Legacy flag fields (isNewStore, isOnboardingSkipped, etc.)
            // are stored on the extended state
            if (payload.isNewStore !== undefined) {
                (state as any).isNewStore = payload.isNewStore;
            }
            if (payload.isOnboardingSkipped !== undefined) {
                (state as any).isOnboardingSkipped = payload.isOnboardingSkipped;
            }
            if (payload.merchantDetails !== undefined) {
                (state as any).merchantDetails = payload.merchantDetails;
            }
            if (payload.metadata !== undefined) {
                (state as any).metadata = payload.metadata;
            }
        },

        /**
         * @deprecated Legacy compat — no-op, included to prevent import errors.
         */
        setLoading: (_state, _action: PayloadAction<boolean>) => {
            // no-op in the new auth flow
        },

        /**
         * @deprecated Legacy compat — use logoutUser thunk instead.
         */
        logout: (state) => {
            state.status = AUTH_STATUS.UNAUTHENTICATED;
            state.user = null;
            state.context = { merchantId: null, storeId: null, roles: [], permissions: [] };
            state.sessionType = null;
            state.authProvider = null;
        },

        /**
         * Set whether the current user needs to set up a password.
         * Driven by meta.needsPasswordSetup from the store details API.
         */
        setNeedsPasswordSetup: (state, action: PayloadAction<boolean>) => {
            state.needsPasswordSetup = action.payload;
        },

        /**
         * Directly update quota from Mantle customer data.
         * Used when Mantle returns fresh quota that the backend hasn't synced yet.
         */
        setQuota: (state, action: PayloadAction<QuotaInfo | null>) => {
            state.quota = action.payload;
        },

        /**
         * @deprecated Legacy compat — no-op, use initializeAuth thunk instead.
         */
        initializeFromToken: () => {
            // no-op — auth is now initialized via initializeAuth thunk
        },
    },
    extraReducers: (builder) => {
        // ── initializeAuth ──
        builder
            .addCase(initializeAuth.pending, (state) => {
                // Only set initializing if we haven't authenticated yet
                if (state.status !== AUTH_STATUS.AUTHENTICATED) {
                    state.status = AUTH_STATUS.INITIALIZING;
                }
            })
            .addCase(initializeAuth.fulfilled, (state, action) => {
                const meData = action.payload as MeResponse | undefined;

                if (!meData) {
                    // Payload missing — treat as unauthenticated
                    state.status = AUTH_STATUS.UNAUTHENTICATED;
                    return;
                }

                if (meData.user) {
                    // Dashboard user with full account
                    state.status = AUTH_STATUS.AUTHENTICATED;
                    state.user = meData.user;
                } else {
                    // Legacy Shopify session (user is null)
                    state.status = AUTH_STATUS.MIGRATING;
                    state.user = null;
                }

                state.context = {
                    merchantId: meData.context?.merchantId ?? null,
                    storeId: meData.context?.storeId ?? null,
                    roles: meData.context?.roles || [],
                    permissions: meData.context?.permissions || [],
                };

                // Preserve existing contexts if API returns empty — prevents
                // dropdowns from disappearing during re-auth / token refresh.
                const incomingMerchants = meData.availableContexts?.merchants;
                const incomingStores = meData.availableContexts?.stores;
                if (incomingMerchants?.length) {
                    state.availableContexts.merchants = incomingMerchants;
                }
                if (incomingStores?.length) {
                    state.availableContexts.stores = incomingStores;
                }

                // Persist availableContexts to localStorage so it survives
                // feature_not_enabled rejections (where /auth/me returns 403
                // and never populates this data). Keyed by userId to prevent
                // cross-account data leakage.
                if (incomingMerchants?.length || incomingStores?.length) {
                    try {
                        localStorage.setItem('available_contexts', JSON.stringify({
                            userId: state.user?.userId || null,
                            merchants: state.availableContexts.merchants,
                            stores: state.availableContexts.stores,
                        }));
                    } catch {}
                }

                // If impersonating and availableContexts is empty, recover from localStorage
                // (saved by MerchantsPage before impersonation redirect)
                if (meData.isImpersonating) {
                    try {
                        const savedCtx = localStorage.getItem('impersonation_context');
                        if (savedCtx) {
                            const parsed = JSON.parse(savedCtx);
                            if (parsed.merchant && state.availableContexts.merchants.length === 0) {
                                state.availableContexts.merchants = [parsed.merchant];
                            }
                            if (parsed.store && state.availableContexts.stores.length === 0) {
                                state.availableContexts.stores = [parsed.store];
                            }
                        }
                    } catch {
                        // ignore parse errors
                    }
                }

                // Super admin with no merchant context: auto-select the first
                // available merchant + its first store so the UI has a default.
                const isSuperAdmin = (meData.context?.roles || []).includes('super_admin');
                if (isSuperAdmin && !state.context.merchantId) {
                    const firstMerchant = state.availableContexts.merchants[0];
                    if (firstMerchant) {
                        state.context.merchantId = firstMerchant.merchantId;
                        const firstStore = state.availableContexts.stores.find(
                            (s) => s.merchantId === firstMerchant.merchantId
                        );
                        if (firstStore) {
                            state.context.storeId = firstStore.storeId;
                        }
                    }
                }

                state.sessionType = (meData.sessionType as SessionType) ?? null;
                state.authProvider = (meData.authProvider as AuthProviderType) ?? null;
                state.isImpersonating = meData.isImpersonating ?? false;
                state.adminUserId = meData.adminUserId ?? null;
                // The backend decides the allowance. This used to keep whichever limit
                // was larger, because the previous provider could report a stale smaller
                // one mid plan-change. That provider is gone, and the rule now does the
                // opposite of what it was for: a limit that goes down — a deal lowered
                // or taken off, a downgrade — was refused, so the client kept quoting an
                // allowance the store no longer has.
                const incomingQuota = meData.quota ?? null;
                if (incomingQuota) {
                    state.quota = incomingQuota;
                }
                // A response that omits quota leaves what is on screen alone, so the
                // banner does not blink out on an unrelated refresh.

                // Mark quota as revalidated so the banner knows fresh data arrived
                state.quotaRevalidated = true;

                // ── Context validation ──
                // If the user was removed from the current workspace while logged in,
                // /auth/me may return a stale merchantId that's no longer in availableContexts.
                // Auto-correct by falling back to the first available merchant, or clear.
                if (
                    state.context.merchantId &&
                    state.availableContexts.merchants.length > 0 &&
                    !state.availableContexts.merchants.some(
                        (m) => m.merchantId === state.context.merchantId
                    )
                ) {
                    // Current merchantId is no longer valid — auto-select first available
                    const fallbackMerchant = state.availableContexts.merchants[0];
                    state.context.merchantId = fallbackMerchant.merchantId;
                    const fallbackStore = state.availableContexts.stores.find(
                        (s) => s.merchantId === fallbackMerchant.merchantId
                    );
                    state.context.storeId = fallbackStore?.storeId ?? null;
                    // Clear roles/permissions — they'll be refreshed on next switch-context
                    state.context.roles = [];
                    state.context.permissions = [];
                } else if (
                    state.context.merchantId &&
                    state.availableContexts.merchants.length === 0 &&
                    !isSuperAdmin
                ) {
                    // User has no available workspaces at all — they were fully removed
                    state.status = AUTH_STATUS.UNAUTHENTICATED;
                    state.user = null;
                    state.context = initialContext;
                    state.availableContexts = initialAvailableContexts;
                    state.sessionType = null;
                    state.authProvider = null;
                    return;
                }
            })
            .addCase(initializeAuth.rejected, (state, action) => {
                // Team plan insufficient — user is still authenticated,
                // just blocked from the workspace. Keep auth state intact
                // so the block screen can render instead of redirecting to login.
                if (action.payload === 'feature_not_enabled') {
                    state.status = AUTH_STATUS.AUTHENTICATED;
                    // Restore availableContexts from localStorage since /auth/me
                    // never returns data on 403. This was saved on last successful /auth/me.
                    // Only restore if the saved data belongs to the current user.
                    if (state.availableContexts.merchants.length === 0) {
                        try {
                            const saved = localStorage.getItem('available_contexts');
                            if (saved) {
                                const parsed = JSON.parse(saved);
                                const currentUserId = state.user?.userId;
                                if (!parsed.userId || parsed.userId === currentUserId) {
                                    if (parsed.merchants?.length) {
                                        state.availableContexts.merchants = parsed.merchants;
                                    }
                                    if (parsed.stores?.length) {
                                        state.availableContexts.stores = parsed.stores;
                                    }
                                }
                            }
                        } catch {}
                    }
                    return;
                }
                state.status = AUTH_STATUS.UNAUTHENTICATED;
                state.user = null;
                state.context = initialContext;
                state.availableContexts = initialAvailableContexts;
                state.sessionType = null;
                state.authProvider = null;
            });

        // ── loginUser ──
        builder
            .addCase(loginUser.fulfilled, (state, action) => {
                const loginData = action.payload as LoginResponse;

                // Persist token to cookies so apiClient interceptor can attach it
                if (loginData.token) {
                    setAuthToken(loginData.token);
                }

                state.status = AUTH_STATUS.AUTHENTICATED;
                state.user = loginData.user;
                state.context = {
                    merchantId: loginData.sessionContext?.merchantId || null,
                    storeId: loginData.sessionContext?.storeId ?? null,
                    roles: loginData.sessionContext?.roles || [],
                    permissions: loginData.sessionContext?.permissions || [],
                };
                state.sessionType = SESSION_TYPES.DASHBOARD_USER;
                state.authProvider = 'password';
            })
            .addCase(loginUser.rejected, (state, action) => {
                // When the backend signals a multi-workspace ambiguity,
                // save the available merchants to state + localStorage.
                // This is critical: if the user then selects a workspace whose
                // plan is downgraded, initializeAuth will get 403 feature_not_enabled
                // and the blocked screen needs hasMultipleWorkspaces to be true.
                const payload = action.payload as any;
                if (payload?.multiWorkspace && Array.isArray(payload.availableMerchants) && payload.availableMerchants.length > 0) {
                    state.availableContexts.merchants = payload.availableMerchants;
                    try {
                        localStorage.setItem('available_contexts', JSON.stringify({
                            merchants: payload.availableMerchants,
                            stores: state.availableContexts.stores,
                        }));
                    } catch {}
                }
            });

        // ── registerUser ──
        builder
            .addCase(registerUser.fulfilled, (state, action) => {
                const regData = action.payload as RegisterResponse;

                // If email verification is required, don't update auth state
                // (user will be redirected to check-your-email page)
                if (regData.requiresVerification) {
                    return;
                }

                // Normal registration — user is auto-logged in
                if (regData.user && regData.sessionContext) {
                    if (regData.token) {
                        setAuthToken(regData.token);
                    }
                    state.status = AUTH_STATUS.AUTHENTICATED;
                    state.user = regData.user;
                    state.context = {
                        merchantId: regData.sessionContext.merchantId,
                        storeId: regData.sessionContext.storeId,
                        roles: regData.sessionContext.roles || [],
                        permissions: regData.sessionContext.permissions || [],
                    };
                    state.sessionType = SESSION_TYPES.DASHBOARD_USER;
                    state.authProvider = 'password';
                }
            });

        // ── logoutUser ──
        builder
            .addCase(logoutUser.fulfilled, (state) => {
                state.status = AUTH_STATUS.UNAUTHENTICATED;
                state.user = null;
                state.context = initialContext;
                state.availableContexts = initialAvailableContexts;
                state.sessionType = null;
                state.authProvider = null;
                state.needsPasswordSetup = false;
            })
            .addCase(logoutUser.rejected, (state) => {
                // Clear state even if API call fails
                state.status = AUTH_STATUS.UNAUTHENTICATED;
                state.user = null;
                state.context = initialContext;
                state.availableContexts = initialAvailableContexts;
                state.sessionType = null;
                state.authProvider = null;
                state.needsPasswordSetup = false;
            });

        // ── switchContext ──
        builder
            .addCase(switchContext.fulfilled, (state, action) => {
                const ctxData = action.payload as SwitchContextResponse;

                // Persist new token when switching context
                if (ctxData.token) {
                    setAuthToken(ctxData.token);
                }

                // Backend may return context under sessionContext, context, or at top level
                const ctx = ctxData.sessionContext || (ctxData as any).context || ctxData;
                if (ctx) {
                    state.context = {
                        merchantId: ctx.merchantId ?? state.context.merchantId,
                        storeId: ctx.storeId ?? state.context.storeId,
                        roles: ctx.roles || state.context.roles || [],
                        permissions: ctx.permissions || state.context.permissions || [],
                    };
                }
                if (ctxData.user) {
                    state.user = ctxData.user;
                }
                // Update availableContexts if the response includes them
                const ac = (ctxData as any).availableContexts;
                if (ac?.merchants?.length) {
                    state.availableContexts.merchants = ac.merchants;
                }
                if (ac?.stores?.length) {
                    state.availableContexts.stores = ac.stores;
                }
            });
    },
});

export const {
    clearAuth,
    updateAvailableContexts,
    setMerchantFeatures,
    updateStoreDetails,
    setLoading,
    logout,
    initializeFromToken,
    setNeedsPasswordSetup,
    setQuota,
} = authSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────

export const selectAuthStatus = (state: { auth: AuthState }) =>
    state.auth.status;

export const selectCurrentUser = (state: { auth: AuthState }) =>
    state.auth.user;

export const selectMerchantId = (state: { auth: AuthState }) =>
    state.auth.context.merchantId;

export const selectStoreId = (state: { auth: AuthState }) =>
    state.auth.context.storeId;

export const selectRoles = (state: { auth: AuthState }) =>
    state.auth.context.roles;

export const selectPermissions = (state: { auth: AuthState }) =>
    state.auth.context.permissions;

export const selectAvailableMerchants = (state: { auth: AuthState }) =>
    state.auth.availableContexts.merchants;

export const selectAvailableStores = (state: { auth: AuthState }) =>
    state.auth.availableContexts.stores;

export const selectCurrentMerchantFeatures = (state: { auth: AuthState }) => {
    const currentMerchantId = state.auth.context.merchantId;
    if (!currentMerchantId) return {};
    const merchant = state.auth.availableContexts.merchants.find(
        (m) => m.merchantId === currentMerchantId
    );
    return merchant?.features || {};
};

export const selectIsMerchantFeatureEnabled = (
    state: { auth: AuthState },
    featureKey: string
) => {
    const features = selectCurrentMerchantFeatures(state);
    return !!features[featureKey];
};

export const selectIsAuthenticated = (state: { auth: AuthState }) =>
    state.auth.status === AUTH_STATUS.AUTHENTICATED;

export const selectIsLegacySession = (state: { auth: AuthState }) =>
    state.auth.sessionType === SESSION_TYPES.LEGACY_SHOPIFY;

export const selectHasStore = (state: { auth: AuthState }) =>
    state.auth.context.storeId !== null;

export const selectSessionType = (state: { auth: AuthState }) =>
    state.auth.sessionType;

export const selectAuthProvider = (state: { auth: AuthState }) =>
    state.auth.authProvider;

export const selectIsImpersonating = (state: { auth: AuthState }) =>
    state.auth.isImpersonating;

export const selectIsSuperAdmin = (state: { auth: AuthState }) =>
    state.auth.context.roles.includes('super_admin');

export const selectNeedsPasswordSetup = (state: { auth: AuthState }) =>
    state.auth.needsPasswordSetup;

export const selectQuota = (state: { auth: AuthState }) =>
    state.auth.quota;

export const selectQuotaRevalidated = (state: { auth: AuthState }) =>
    state.auth.quotaRevalidated;

export default authSlice.reducer;
